import { Link } from '@tanstack/react-router';
import type { AlertaCustoFicha, FichaResponse } from '@triade/contracts';
import { formatarData, formatarCusto, formatarPercentual } from '@/lib/format';
import { mesCurto, ROTULO_ERRO_CUSTO } from './tipos';

/**
 * Avisos do custo da ficha NA DATA como a API devolveu (so leitura): estado (sem CPM, ciclo, profundidade), "CPM antigo
 * (mes) e o bloco de referencia XARP. Os totais ficam no resumo da tela (vindos da API).
 * Sem `custo.ver` (PCP) o bloco de R$ nao aparece, mas os alertas continuam (a API manda isso). Semiacabados: secao propria.
 */
export function PainelCustoFicha({ ficha, comCusto }: { ficha: FichaResponse; comCusto: boolean }) {
  const alertas = ficha.alertasCusto ?? [];
  const estado = ficha.estadoCusto ?? 'OK';
  const antigos = alertas.filter((a) => a.code === 'CPM_ANTIGO');
  const custo = ficha.custoNaData;

  return (
    <div className="flex flex-col gap-4">
      {comCusto && custo?.bruto !== undefined && (
        <section aria-label="Composição do custo líquido" className="rounded-2xl border border-border bg-card p-5" data-testid="custo-liquido-ficha">
          <h3 className="text-title font-semibold">Custo líquido da ficha</h3>
          <p className="mt-1 text-caption text-ink-muted">Bruto menos o crédito dos subprodutos, mais a destinação dos resíduos. Valores vindos da API.</p>
          <dl className="mt-3 grid grid-cols-[1fr_auto] gap-x-4 gap-y-2 text-small">
            <dt className="text-ink-secondary">Bruto (antes das saídas)</dt>
            <dd className="tabular text-right" data-testid="custo-bruto">{formatarCusto(custo.bruto)}</dd>
            <dt className="text-ink-secondary">(−) Crédito de subproduto</dt>
            <dd className="tabular text-right" data-testid="custo-credito">{formatarCusto(custo.credito)}</dd>
            <dt className="text-ink-secondary">(+) Destinação de resíduo</dt>
            <dd className="tabular text-right" data-testid="custo-destinacao">{formatarCusto(custo.destinacao)}</dd>
            <dt className="font-semibold">Custo líquido</dt>
            <dd className="tabular text-right font-semibold" data-testid="custo-liquido">{formatarCusto(custo.total)}</dd>
          </dl>
        </section>
      )}
      {(estado === 'CICLO_NA_FICHA' || estado === 'PROFUNDIDADE_FICHA_EXCEDIDA') && (
        <Aviso titulo={estado === 'CICLO_NA_FICHA' ? 'Ciclo na cascata de semiacabados' : 'Cascata de semiacabados profunda demais'} tom="erro" codigo={estado}>
          {ROTULO_ERRO_CUSTO[estado]} Sem custo na data, esta ficha não entra na formação nem no orçamento até o ciclo ser desfeito.
          {textoAlertas(alertas, estado)}
        </Aviso>
      )}

      {estado === 'SAIDA_INVALIDA' && (
        <Aviso titulo="Saídas da ficha deixam o custo líquido negativo" tom="erro" codigo="SAIDA_INVALIDA">
          <p>
            {textoAlertas(alertas, estado).trim() || 'Uma saída (subproduto ou resíduo) tem quantidade ou valor inválido.'}
          </p>
          <p className="mt-2">O que fazer: reduza a quantidade do subproduto ou o valor de recuperação (Item › Recuperação) e salve; o custo volta sozinho.</p>
          <div className="mt-2 font-medium" data-testid="custo-indisponivel">
            Custo: indisponível
          </div>
        </Aviso>
      )}

      {estado === 'SEM_CPM' && (
        <Aviso titulo="Sem CPM para calcular as etapas" tom="aviso" codigo="SEM_CPM">
          <p>
            Um ou mais centros de custo ainda não têm custo por minuto até o mês da data, então as etapas não têm custo. Cadastre o CPM do
            centro em Centros de custo; a ficha volta a calcular sozinha.
          </p>
          {alertas.filter((a) => a.code === 'CPM_AUSENTE').length > 0 && (
            <ul className="mt-2 list-disc pl-5" data-testid="centros-sem-cpm">
              {alertas
                .filter((a) => a.code === 'CPM_AUSENTE')
                .map((a, i) => (
                  <li key={`${a.centroCodigo ?? 'centro'}-${i}`}>
                    <span className="tabular font-medium">{a.centroCodigo ?? 'Centro'}</span>
                    {a.message ? ` · ${a.message}` : ''}
                  </li>
                ))}
            </ul>
          )}
        </Aviso>
      )}

      {antigos.length > 0 && (
        <section aria-label="CPM antigo" className="rounded-2xl border border-[#FED7AA] bg-warning-bg px-4 py-3 text-small text-warning-ink">
          <div className="font-semibold">Custo por minuto de mês anterior</div>
          <p className="mt-1">
            Estas etapas usam o CPM do último mês disponível do centro, porque o mês mais recente ainda não foi salvo para ele.
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {antigos.map((a, i) => (
              <span key={`${a.centroCodigo ?? ''}-${a.competencia ?? ''}-${i}`} data-testid="chip-cpm-antigo-ficha" className="rounded-full bg-card px-2.5 py-0.5 text-caption font-medium">
                CPM antigo ({mesCurto(a.competencia)}) · {a.centroCodigo ?? 'centro'}
              </span>
            ))}
          </div>
        </section>
      )}

      {comCusto && ficha.variacaoCusto?.variou && (
        <section role="status" aria-label="Variação do custo" data-testid="aviso-variacao-custo" className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-warning-dot/40 bg-warning-bg px-4 py-3 text-small text-warning-ink">
          <div>
            <div className="font-semibold" data-testid="variacao-custo-texto">
              {ficha.variacaoCusto.percentual !== null
                ? `O custo mudou ${formatarPercentual(ficha.variacaoCusto.percentual)} desde ${formatarData(ficha.variacaoCusto.desde)}`
                : `O custo mudou desde ${formatarData(ficha.variacaoCusto.desde)}`}
            </div>
            <dl className="mt-2 grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
              <dt>{ficha.variacaoCusto.origem === 'XARP' ? 'Referência XARP' : 'Custo gravado'} ({formatarData(ficha.variacaoCusto.desde)})</dt>
              <dd className="tabular text-right" data-testid="variacao-custo-base">{formatarCusto(ficha.variacaoCusto.baseCusto)}</dd>
              <dt>Custo de hoje</dt>
              <dd className="tabular text-right" data-testid="variacao-custo-hoje">{formatarCusto(ficha.variacaoCusto.hoje)}</dd>
            </dl>
          </div>
          <Link to="/precificar/lista" className="shrink-0 font-medium text-primary hover:underline" data-testid="variacao-custo-revisar">
            Revisar o preço
          </Link>
        </section>
      )}

      {comCusto && ficha.referenciaXarp && (
        <section aria-label="Referência XARP" className="rounded-2xl border border-border bg-card p-5" data-testid="referencia-xarp">
          <h3 className="text-title font-semibold">Custo de referência XARP × atual</h3>
          <p className="mt-1 text-caption text-ink-muted">Valor gravado pelo XARP (somente leitura) comparado ao custo na data calculado aqui.</p>
          <dl className="mt-3 grid grid-cols-[1fr_auto] gap-x-4 gap-y-2 text-small">
            <dt className="text-ink-secondary">XARP ({formatarData(ficha.referenciaXarp.dataReferencia)})</dt>
            <dd className="tabular text-right" data-testid="xarp-custo">{formatarCusto(ficha.referenciaXarp.custo)}</dd>
            <dt className="text-ink-secondary">Atual</dt>
            <dd className="tabular text-right" data-testid="xarp-atual">{custo ? formatarCusto(custo.total) : '—'}</dd>
            <dt className="text-ink-secondary">Diferença (atual − XARP)</dt>
            <dd className="tabular text-right" data-testid="xarp-diferenca">{formatarCusto(ficha.referenciaXarp.diferenca ?? null)}</dd>
          </dl>
        </section>
      )}
    </div>
  );
}

function textoAlertas(alertas: AlertaCustoFicha[], estado: string): string {
  const mensagens = alertas.filter((a) => a.code === estado && a.message).map((a) => a.message);
  return mensagens.length > 0 ? ` ${mensagens.join(' ')}` : '';
}

function Aviso({ titulo, tom, codigo, children }: { titulo: string; tom: 'erro' | 'aviso'; codigo: string; children: React.ReactNode }) {
  return (
    <section
      role={tom === 'erro' ? 'alert' : 'status'}
      data-testid={`estado-custo-${codigo}`}
      className={
        tom === 'erro'
          ? 'rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink'
          : 'rounded-xl border border-warning-dot/40 bg-warning-bg px-4 py-3 text-small text-warning-ink'
      }
    >
      <div className="font-semibold">{titulo}</div>
      <div className="mt-1">{children}</div>
    </section>
  );
}

